import { computed, ref, Ref, watch, ComputedRef, onUnmounted } from "vue";
import type { DrilldownNode } from "@/shared/model/drilldown-node";
import { acceptHMRUpdate, defineStore } from "pinia";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { EventState } from "@/shared/model/enums/event-state";
import { StartTimeRange } from "@/shared/model/enums/start-time-range";
import { Sport } from "@/shared/model/enums/sport";
import { Event, EventDrilldown, EventDrilldownDetail } from "@/shared/model/event";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import useTrefikApi from "@/api/composables/use-trefik-api";
import removeAccents from "@/shared/utils/remove-accents";
import usePrematchRouteIds from "../composables/use-prematch-route-ids";
import useMarketGrouping from "@/live/composables/use-market-grouping";
import { PrematchStaticCategories } from "../model/enums/prematch-static-categories";
import useUtils from "@/shared/composables/use-utils";
import { TrefikSports } from "../model/enums/trefik";
import { TrefikStatsUniversal } from "@/prematch/model/trefik/trefik-universal";
import { analyticsPageObj } from "@/shared/composables/analytics/use-general-entities";
import { Constants } from "@/shared/model/enums/analytics/constants";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { waitUntilTrue } from "@/shared/utils/wait";
import { usePrematchFilters } from "./prematch-filters";
import useMock from "@/live/composables/use-mock";
import { Market, MarketGroup } from "@/shared/model/market";
import DrilldownNodeUtils from "@/shared/utils/drilldown-node-utils";
import { usePrematchEvents } from "./prematch-events";
interface Params {
    eventState: EventState;
    startTimeRange?: StartTimeRange;
}

//added this interface to make conneting events to leagues to countries to sports easier
export interface PrematchDrilldownNode extends DrilldownNode {
    route?: string;
    countryId?: string;
    sportId?: string;
    trefikData?: TrefikStatsUniversal;
    trending?: boolean;
    showInFavouriteEvents?: boolean;
    showInFavouriteLeagues?: boolean;
    lowestDisplayOrderMarket?: Market | MarketGroup;
}

export interface PrematchEventDrilldown extends EventDrilldownDetail {
    trending?: boolean;
    favourite?: boolean;
}

export interface EventsAndOutrights {
    events: EventDrilldown[];
    outrights: Event[];
}

export const usePrematchStore = defineStore(
    "usePrematchStore",
    () => {
        const refreshTimer = ref<number | null>(null);
        const REFRESH_INTERVAL = 60_000;

        const favouritesStore = useFavouritesStore();
        const { getTrefikLeagues } = useTrefikApi();
        const { isEventInTimeRange, sortByDisplayOrder, sortByNegativeDisplayOrder } = useUtils();
        const { getHeaders, getOddsByHeaders } = useMarketGrouping();
        const { getRouteIds } = usePrematchRouteIds();
        const trefikApi = useTrefikApi();
        const prematchFilters = usePrematchFilters();
        const { getMarketPreviewConfig } = useMock();
        const { failedEvents } = usePrematchEvents();
        //state

        //loader states
        const prematchLeftLoaderActive: Ref<boolean> = ref(false);
        const favoriteEventsLoaded: Ref<boolean> = ref(false);
        const favoriteLeaguesLoaded: Ref<boolean> = ref(false);
        const sportsLoaded: Ref<boolean> = ref(false);
        const storeLoaded: Ref<boolean> = ref(false);
        const liveEventsLoadedLeaguesIds: Ref<string[]> = ref([]);

        const sportsError: Ref<boolean> = ref(false);
        const loadingLeagues: Ref<boolean> = ref(false);

        const sportsCount: Ref<number> = ref(10); // persisted

        const outrights: Ref<Event[]> = ref([]);

        const events: Ref<PrematchEventDrilldown[]> = ref([]);
        const leagues: Ref<PrematchDrilldownNode[]> = ref([]);
        const countries: Ref<PrematchDrilldownNode[]> = ref([]);
        const sports: Ref<PrematchDrilldownNode[]> = ref([]);
        const trefikLeagues: Ref<string[]> = ref([]);
        //I left this here because it's used in a lot of the components but FIXME: refactor this to use only the prematchFilters version
        const activeTimeFilter: ComputedRef<StartTimeRange | null> = computed(
            () => prematchFilters.getActiveTimeFilter,
        );

        const filteredIds: Ref<string[]> = ref([]);

        const params: Params = {
            eventState: EventState.OpenEvent,
            startTimeRange: prematchFilters.activeTimeFilter ?? undefined,
        };

        const { getCachedPrematchLeagues, getEventsDrilldown, getOutrightsEvents, getSazkaLeagues } = useOpenbetApi();

        const fetchLeagues = async (params: Params) => {
            try {
                // Initialize arrays for storing data
                const newSports = [];
                const newCountries = [];
                const newLeagues = [];
                const newFilteredIds = [];

                // FIRST CALL: Get all leagues without time filter
                const allLeaguesResponse = await getSazkaLeagues({
                    eventState: params.eventState,
                    startTimeRange: undefined,
                });

                processLeaguesResponse(allLeaguesResponse, newSports, newCountries, newLeagues);

                // SECOND CALL: Only if time filter is active
                if (params.startTimeRange) {
                    // Make second call to get filtered leagues. We need both calls to get the correct league name when the time filter is applied and no events are found.
                    const filteredResponse = await getSazkaLeagues(params);

                    filteredResponse?.forEach(({ drilldownNodes: sportDrilldownNodes, id: sportId }) => {
                        newFilteredIds.push(sportId);

                        sportDrilldownNodes?.forEach(({ drilldownNodes: countryDrilldownNodes, id: countryId }) => {
                            newFilteredIds.push(countryId);

                            countryDrilldownNodes?.forEach(({ id: leagueId }) => {
                                newFilteredIds.push(leagueId);
                            });
                        });
                    });
                }

                sports.value = newSports;
                countries.value = newCountries;
                leagues.value = newLeagues;
                filteredIds.value = newFilteredIds;
            } catch (error) {
                logException("prematch-sports", error, {
                    message: "An error occurred while fetching sports",
                    ...params,
                });
                sportsError.value = true;
            } finally {
                storeLoaded.value = true;
                loadTrefikLeagues();
            }
        };

        function processLeaguesResponse(response, sportsArray, countriesArray, leaguesArray) {
            //We need to preserve the existing enhancements for leagues, sports and countries, might refactor later to detach these enhancements from the top level
            const existingLeaguesMap = new Map();
            const existingSportsMap = new Map();
            const existingCountriesMap = new Map();

            leagues.value.forEach((league) => {
                existingLeaguesMap.set(league.id, league);
            });

            sports.value.forEach((sport) => {
                existingSportsMap.set(sport.id, sport);
            });

            countries.value.forEach((country) => {
                existingCountriesMap.set(country.id, country);
            });

            response?.forEach(({ drilldownNodes: sportDrilldownNodes, id: sportId, ...sport }) => {
                // Get existing sport data
                const existingSport = existingSportsMap.get(sportId);

                sportsArray.push({
                    ...sport,
                    id: sportId,
                    route: sportId,
                    // Preserve important properties
                    oddsHeaders: existingSport?.oddsHeaders ?? [],
                    lowestDisplayOrderMarket: existingSport?.lowestDisplayOrderMarket ?? null,
                });

                sportDrilldownNodes?.forEach(({ drilldownNodes: countryDrilldownNodes, id: countryId, ...country }) => {
                    // Get existing country data
                    const existingCountry = existingCountriesMap.get(countryId);

                    countriesArray.push({
                        ...country,
                        id: countryId,
                        route: `${sportId}-${countryId}`,
                        sportId,
                        // Preserve important properties
                        oddsHeaders: existingCountry?.oddsHeaders ?? [],
                    });

                    countryDrilldownNodes?.forEach(({ id: leagueId, ...league }) => {
                        // Get existing league data
                        const existingLeague = existingLeaguesMap.get(leagueId);

                        leaguesArray.push({
                            ...league,
                            id: leagueId,
                            route: `${sportId}${DrilldownNodeUtils.isSportMultilevel(league.code) ? `-${countryId}` : ""}/${leagueId}`,
                            sportId,
                            countryId,
                            // Preserve important properties
                            eventsAndOutrightsLoaded: existingLeague?.eventsAndOutrightsLoaded ?? false,
                            errorLoadingEvents: existingLeague?.errorLoadingEvents ?? false,
                            oddsHeaders: existingLeague?.oddsHeaders ?? [],
                            selectedOdd: existingLeague?.selectedOdd ?? null,
                            trefikData: existingLeague?.trefikData ?? null,
                        });
                    });
                });
            });

            // Handle filtered ids separately outside this function since they depend on params
        }
        const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
            await waitUntilTrue(storeLoaded, time, timeout).catch(() => {
                logException("prematch-sports", undefined, {
                    message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
                });
            });
        };

        const waitUntilEventInitialized = async (
            eventId: string | number,
            time: number = 100,
            timeout: number = 10000,
        ) => {
            await waitUntilTrue(
                () =>
                    events.value.some((event) => event.id === eventId.toString()) ||
                    failedEvents.includes(eventId.toString()),
                time,
                timeout,
            ).catch(() => {
                logException("prematch-sports", undefined, {
                    message: `Event initialization timed out. Timeout interval ${timeout} breached.`,
                });
            });
        };

        const startPeriodicRefresh = () => {
            stopPeriodicRefresh();

            refreshTimer.value = window.setInterval(() => {
                fetchLeagues({
                    eventState: EventState.OpenEvent,
                    startTimeRange: activeTimeFilter.value || undefined,
                });
            }, REFRESH_INTERVAL);
        };

        const stopPeriodicRefresh = () => {
            if (refreshTimer.value !== null) {
                window.clearInterval(refreshTimer.value);
                refreshTimer.value = null;
            }
        };

        //filter and sort events by common rules
        const getEvents = computed(() => {
            const filteredEvents = events.value.filter(
                (event) => event.active && isEventInTimeRange(activeTimeFilter.value, event.startTime),
            );
            if (getRouteIds.value.sportId === PrematchStaticCategories.MOST_POPULAR) {
                return sortByDisplayOrder(events.value);
            }
            return sortByDisplayOrder(filteredEvents);
        });

        const getEventsByLeague = (leagueId: string) => {
            return getRouteIds.value.leagueId
                ? getEvents.value.filter((event) => event.competitionDrilldownTagId === leagueId)
                : getEvents.value.filter(
                      (event) => event.competitionDrilldownTagId === leagueId && event.liveNow !== true,
                  );
        };

        const getOutrightsByLeague = (leagueId: string): Event[] => {
            return outrights.value.filter((outright) => outright.competitionDrilldownTagId === leagueId);
        };

        const getSportById = (sportId: string): PrematchDrilldownNode | undefined => {
            return sports.value.find((sport) => sport.id === sportId);
        };

        const getEventById = (eventId: string): EventDrilldownDetail | undefined => {
            return events.value.find((event) => event.id === eventId);
        };

        const getLeagueById = (leagueId: string): PrematchDrilldownNode | undefined => {
            return leagues.value.find((league) => league.id === leagueId);
        };

        const getLeaguesCountBySport = (sportId: string): number => {
            return filteredLeagues.value.filter((league) => league.sportId === sportId).length;
        };

        const getEventCountsByLeagueAndSelectedTime = (leagueId: string): number => {
            const league = filteredLeagues.value.find((l) => l.id === leagueId);

            if (!league) {
                const country = filteredCountries.value.find((c) => c.id === leagueId);
                if (country) {
                    return getLeaguesByCountry(leagueId).reduce((acc, league) => {
                        return acc + getEventCountsByLeagueAndSelectedTime(league.id);
                    }, 0);
                }
            }
            if (!activeTimeFilter.value) {
                return league?.eventCounts.length > 0 ? Math.max(...league.eventCounts.flatMap(Object.values)) : 0;
            }

            return (
                league?.eventCounts.find((count) => {
                    const hasActiveTimeFilterValue = count[activeTimeFilter.value] !== undefined;
                    return hasActiveTimeFilterValue;
                })?.[activeTimeFilter.value] || 0
            );
        };

        const getLeagues = computed(() => {
            const filteredLeagues = leagues.value.filter(
                (league) => filteredIds.value.length === 0 || filteredIds.value.includes(league.id),
                //&& league.eventCounts.length > 0,  TODO: Since this value is not working f.e. for price boost, it's unfortunately not possible to use it
            );

            return sortByNegativeDisplayOrder(filteredLeagues);
        });

        const activeSport = computed(
            () => sports.value.find((sport) => sport.id === getRouteIds.value.sportId) || null,
        );

        const getLeaguesBySport = (sportId: string): PrematchDrilldownNode[] => {
            return getLeagues.value.filter((league) => league.sportId === sportId);
        };

        const getLeaguesByCountry = (countryId: string): PrematchDrilldownNode[] => {
            if (!filteredIds.value.length) return leagues.value.filter((league) => league.countryId === countryId);
            return getLeagues.value.filter(
                (league) => league.countryId === countryId && filteredIds.value.includes(league.id),
            );
        };

        const getCountriesBySport = (sportId: string): PrematchDrilldownNode[] => {
            if (!filteredIds.value.length) return countries.value.filter((country) => country.sportId === sportId);
            return countries.value.filter(
                (country) => country.sportId === sportId && filteredIds.value.includes(country.id),
            );
        };

        const getSpecialCategories: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
            if (!filteredIds.value.length) return sports.value.filter((sport) => sport.displayOrder <= -2000);
            return sports.value.filter((sport) => filteredIds.value.includes(sport.id) && sport.displayOrder <= -2000);
        });

        const getEventsCountFromLeagues = (leagues: DrilldownNode[]): number => {
            let totalCount = 0;

            const getCount = (eventCounts: { [key: string]: number }[], filter: string | null): number => {
                // If no filter is selected, return combined LIVE + FUTURE counts
                if (!filter) {
                    const liveCount = eventCounts.find((count) => Object.keys(count)[0] === "LIVE");
                    const futureCount = eventCounts.find((count) => Object.keys(count)[0] === "FUTURE");
                    return (futureCount ? futureCount["FUTURE"] : 0) + (liveCount ? liveCount["LIVE"] : 0);
                }

                const eventCount = eventCounts.find((count) => Object.keys(count)[0] === filter);
                if (eventCount) {
                    return eventCount[filter];
                } else {
                    const liveCount = eventCounts.find((count) => Object.keys(count)[0] === "LIVE");
                    const futureCount = eventCounts.find((count) => Object.keys(count)[0] === "FUTURE");
                    return (futureCount ? futureCount["FUTURE"] : 0) + (liveCount ? liveCount["LIVE"] : 0);
                }
            };

            const traverseNodes = (nodes: DrilldownNode[]) => {
                if (!nodes) return;
                nodes.forEach((node) => {
                    if (node.eventCounts) {
                        totalCount += getCount(node.eventCounts, activeTimeFilter.value);
                    }
                    if (node.drilldownNodes) {
                        traverseNodes(node.drilldownNodes);
                    }
                });
            };

            traverseNodes(leagues);
            return totalCount;
        };

        const getEventsCountFromLeague = (leagueId: string): number => {
            const league = leagues.value.find((l) => l.id === leagueId);
            return league ? getEventsCountFromLeagues([league]) : 0;
        };

        const getEventsCountFromCountry = (countryId: string): number => {
            const countryLeagues = leagues.value.filter((c) => c.countryId === countryId);
            return countryLeagues && countryLeagues.length ? getEventsCountFromLeagues(countryLeagues) : 0;
        };

        const filteredSports: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
            if (!filteredIds.value.length)
                return sports.value
                    .filter((sport) => sport.displayOrder > -2000)
                    ?.map((sport) => ({
                        ...sport,
                        isFavorite: favouritesStore.isFavouriteSport(sport.id),
                    }));

            return sports.value
                .filter((sport) => filteredIds.value.includes(sport.id) && sport.displayOrder > -2000)
                ?.map((sport) => ({
                    ...sport,
                    isFavorite: favouritesStore.isFavouriteSport(sport.id),
                }));
        });

        const filteredSportsNoDisplayOrder: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
            // Filter leagues to remove those that have only live events
            const filteredLeagues = leagues.value.filter((league) => {
                const liveEventCount = league.eventCounts.find((count) => count.LIVE);
                return !(league.eventCounts.length === 1 && liveEventCount);
            });

            // Filter sports to include only those that have at least one league remaining
            const filteredSports = sports.value.filter((sport) => {
                const sportLeagues = filteredLeagues.filter((league) => league.sportId === sport.id);
                return sportLeagues.length > 0;
            });

            if (!filteredIds.value.length) {
                return filteredSports.map((sport) => ({
                    ...sport,
                    isFavorite: favouritesStore.isFavouriteSport(sport.id),
                }));
            }

            return filteredSports
                .filter((sport) => filteredIds.value.includes(sport.id))
                .map((sport) => ({
                    ...sport,
                    isFavorite: favouritesStore.isFavouriteSport(sport.id),
                }));
        });

        const filteredLeagues: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
            if (!filteredIds.value.length) return leagues.value;
            return leagues.value.filter((league) => filteredIds.value.includes(league.id));
        });

        const filteredCountries: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
            if (!filteredIds.value.length) return countries.value;
            return countries.value.filter((country) => filteredIds.value.includes(country.id));
        });

        const isFilterLoading: ComputedRef<boolean> = computed(() => prematchLeftLoaderActive.value);

        const leagueHasTrefik = (sportName: string, leagueName: string): boolean => {
            const formattedSportName = removeAccents(sportName)
                ?.toLowerCase()
                .replace(/ /g, "-")
                .replace(".", "-")
                .replace(",", "-");
            const formattedLeagueName = removeAccents(leagueName)
                ?.toLowerCase()
                .replace(/ /g, "-")
                .replace(".", "-")
                .replace(",", "-");

            return trefikLeagues.value.includes(`${formattedSportName}/${formattedLeagueName}`);
        };

        async function loadTrefikLeagues() {
            try {
                trefikLeagues.value = await getTrefikLeagues();
            } catch {
                trefikLeagues.value = [];
            }
        }

        const getEventsAndOutrightsBySportCodeAndLeagueIds = async (
            sportCode: string,
            leagueIds: number[],
            groupedMarkets: boolean,
            isIncreasedOdd: boolean,
            liveNow?: boolean,
        ): Promise<EventsAndOutrights> => {
            const customGroupesIncluded = Array.from(
                new Set(getMarketPreviewConfig(sportCode).map((m) => m.market_group_code)),
            );
            const sortCodesIncluded = Array.from(new Set(getMarketPreviewConfig(sportCode).map((m) => m.sort_code)));

            let loadedEvents = null;
            let loadedOutrights = null;

            // Trying to load using our faster v1 api first
            try {
                const response = await getCachedPrematchLeagues(leagueIds, { wsData: { update: true } });

                // Check if we need to use the fallback
                if (response.needsFallback) {
                    logException("prematch-sports", response.error, {
                        message: "Failed to load events with v1 API, falling back to getEventsDrilldown",
                        leagueIds,
                        sportCode,
                        responseLeaguesLength: response.data.length,
                    });

                    // Fall back to the slower method if we receive no content
                    try {
                        const fallbackResponse = await getEventsDrilldown(
                            {
                                drilldownTagIds: leagueIds,
                                marketGroupTypesIncluded: customGroupesIncluded || undefined,
                                marketsSortsIncluded: sortCodesIncluded || undefined,
                                groupedMarkets,
                                liveNow,
                                sazkabetTransformations: ["availability", "groupedMarkets", "oddsHeadersSports"],
                            },
                            { wsData: { update: true } },
                        );
                        loadedEvents = fallbackResponse;
                        if (!fallbackResponse.length) {
                            logException("prematch-sports", response.error, {
                                message: "No events found in fallback method",
                                leagueIds,
                                sportCode,
                            });
                        }
                    } catch (fallbackError) {
                        logException("prematch-sports", fallbackError, {
                            message: "Failed to load events with both methods",
                            leagueIds,
                            sportCode,
                        });
                    }
                } else {
                    //if no need to fallback, we can use the response directly
                    loadedEvents = response.data;
                }
            } catch (error) {
                // Something went wrong with the v1 API request itself - use the fallback method
                logException("prematch-sports", error, {
                    message: "Failed to load events with v1 API",
                    leagueIds,
                    sportCode,
                });

                // Fall back to the original method
                try {
                    loadedEvents = await getEventsDrilldown(
                        {
                            drilldownTagIds: leagueIds,
                            marketGroupTypesIncluded: customGroupesIncluded ?? undefined,
                            marketsSortsIncluded: sortCodesIncluded ?? undefined,
                            groupedMarkets,
                            liveNow,
                            sazkabetTransformations: ["availability", "groupedMarkets", "oddsHeadersSports"],
                        },
                        { wsData: { update: true } },
                    );
                } catch (fallbackError) {
                    logException("prematch-sports", fallbackError, {
                        message: "Failed to load events with both methods",
                        leagueIds,
                        sportCode,
                    });
                }
            }

            // Process loaded events if they exist
            if (loadedEvents) {
                // Need to add parentMarket to every single outcome (for increased odds afaik)
                loadedEvents = processLoadedEvents(loadedEvents, isIncreasedOdd);
            }

            //TODO: think of a better way - this is also slow since its directly through OB api
            //There is an information on events if they have outrights or not - but on the event it does not make much sense but might be able to do something with it to load outrights
            try {
                loadedOutrights = await getOutrightsEvents({
                    drilldownTagIds: leagueIds,
                });

                // Process outright data
                if (loadedOutrights) {
                    loadedOutrights = loadedOutrights
                        .map((evt) => ({
                            ...evt,
                            markets: evt.markets
                                .map((m) => ({
                                    ...m,
                                    outcomes: m.outcomes
                                        .map((o) => ({
                                            ...o,
                                            parentMarket: {
                                                handicapValue: m.handicapValue,
                                                eventId: m.eventId,
                                                active: m.active,
                                                status: m.status,
                                            },
                                        }))
                                        .sort((a, b) => {
                                            if (a.displayOrder === b.displayOrder) {
                                                return a.prices[0].decimal - b.prices[0].decimal;
                                            }
                                            return a.displayOrder - b.displayOrder;
                                        }),
                                }))
                                .sort((a, b) => a.displayOrder - b.displayOrder),
                        }))
                        .sort((a, b) => {
                            if (a.displayOrder === b.displayOrder) {
                                return a.name.length - b.name.length;
                            }
                            return a.displayOrder - b.displayOrder;
                        });
                }
            } catch (error) {
                logException("prematch-sports", error, {
                    message: "Failed to load outrights data",
                    leagueIds,
                    sportCode,
                });
                loadedOutrights = [];
            }

            return {
                events: loadedEvents ?? [],
                outrights: loadedOutrights ?? [],
            };
        };

        const processAdditionalLiveData = async (sportCode: string): Promise<void> => {
            const currentLeague = leagues.value.find((l) => l.id === getRouteIds.value.leagueId);
            if (!currentLeague?.eventCounts?.length) return;

            const updatedSports: Set<string> = new Set();
            const updatedCountries: Set<string> = new Set();

            leagues.value.forEach(async (league) => {
                if (currentLeague) {
                    updatedSports.add(league.sportId);
                    updatedCountries.add(league.countryId);
                }
            });

            if (!getRouteIds.value.leagueId || !currentLeague) return;

            const customGroupesIncluded = Array.from(
                new Set(getMarketPreviewConfig(sportCode).map((m) => m.market_group_code)),
            );
            const sortCodesIncluded = Array.from(new Set(getMarketPreviewConfig(sportCode).map((m) => m.sort_code)));

            let loadedEvents = null;
            try {
                loadedEvents = await getEventsDrilldown(
                    {
                        drilldownTagIds: [currentLeague.id],
                        marketGroupTypesIncluded: customGroupesIncluded ?? undefined,
                        marketsSortsIncluded: sortCodesIncluded ?? undefined,
                        groupedMarkets: true,
                        liveNow: true,
                        sazkabetTransformations: ["availability", "groupedMarkets", "oddsHeadersSports"],
                    },
                    { wsData: { update: true } },
                );

                loadedEvents = processLoadedEvents(loadedEvents, false);
                if (!loadedEvents) return;
                setEvents(loadedEvents, events.value);
                updateLeagues([Number(currentLeague.id)], leagues.value, getEventsByLeague);
                setHeadersSports(updatedSports, sports.value, events.value);
                setHeadersCountries(updatedCountries, countries.value, events.value);
            } catch (e) {
                logException("prematch-sports", e, {
                    message: `An error occurred while fetching live events for league ${currentLeague.name} with ID: ${currentLeague.id}`,
                    leagueId: currentLeague.id,
                    leagueName: currentLeague.name,
                });
            } finally {
                liveEventsLoadedLeaguesIds.value.push(currentLeague.id);
            }
        };

        function processLoadedEvents(loadedEvents: EventDrilldown[], isIncreasedOdd: boolean): EventDrilldown[] {
            if (isIncreasedOdd) {
                loadedEvents = loadedEvents?.map((evt) => ({
                    ...evt,
                    markets: evt.markets?.map((m) => ({
                        ...m,
                        outcomes: m.outcomes?.map((o) => ({
                            ...o,
                            parentMarket: {
                                handicapValue: m.handicapValue,
                                eventId: m.eventId,
                                active: m.active,
                                status: m.status,
                            },
                        })),
                    })),
                }));
            }

            // Filter out outcomes with name "null" or "12" in groupedMarkets with name "CUSTOM"
            return loadedEvents?.map((evt) => ({
                ...evt,
                groupedMarkets: evt.groupedMarkets?.map((gm) => ({
                    ...gm,
                    outcomes:
                        gm.groupCode === "CUSTOM"
                            ? gm.outcomes.filter((o) => o.name !== null && o.name !== "12")
                            : gm.outcomes,
                })) as (Market | MarketGroup)[],
            }));
        }

        async function loadLeagues(leaguesToLoad: number[]): Promise<void> {
            loadingLeagues.value = true;
            const updatedSports: Set<string> = new Set();
            const updatedCountries: Set<string> = new Set();
            leagues.value.forEach(async (league) => {
                if (leaguesToLoad.includes(Number(league.id))) {
                    if (getRouteIds.value.leagueId) {
                        try {
                            const trefikData = await trefikApi.getTrefikLeagueStats({
                                sport: sports.value.find((sport) => sport.id === league.sportId)?.name as TrefikSports,
                                league: league?.name,
                            });
                            league.trefikData = trefikData;
                        } catch (error) {
                            logException("prematch-sports", error, {
                                message: "An error occurred while fetching trefik data for league",
                                leagueId: league.id,
                                leagueName: league.name,
                            });
                        }
                    }

                    updatedSports.add(league.sportId);
                    updatedCountries.add(league.countryId);
                }
            });

            let groupedMarkets = true;
            const currentSportCode = sports.value.find((sport) => sport.id === getRouteIds.value.sportId)?.code;

            // NAVYSENE KURZY
            const isIncreasedOdd = currentSportCode === Sport.PriceBoost;
            if (isIncreasedOdd) {
                groupedMarkets = false;
            }

            try {
                const response: EventsAndOutrights = await getEventsAndOutrightsBySportCodeAndLeagueIds(
                    currentSportCode,
                    leaguesToLoad,
                    groupedMarkets,
                    isIncreasedOdd,
                    getRouteIds.value.leagueId ? undefined : false,
                );
                if (response.events && response.events.length > 0) {
                    setEvents(response.events, events.value);
                }

                if (response.outrights && response.outrights.length > 0) {
                    setOutrights(response.outrights, outrights.value);
                }
                updateLeagues(leaguesToLoad, leagues.value, getEventsByLeague);
                setHeadersSports(updatedSports, sports.value, events.value);
                setHeadersCountries(updatedCountries, countries.value, events.value);
            } catch (error) {
                updateLeagues(leaguesToLoad, leagues.value, getEventsByLeague, true);
                logException("prematch-sports", error, {
                    message: "An error occurred while fetching leagues",
                    leagueIds: leaguesToLoad,
                });
            }

            processAdditionalLiveData(currentSportCode);
        }

        async function updateLeagues(
            leaguesToLoad: number[],
            leaguesValue: PrematchDrilldownNode[],
            eventsByLeagueFunction: (id: string) => PrematchEventDrilldown[],
            error: boolean = false,
        ) {
            leaguesValue
                .filter((l) => leaguesToLoad.includes(Number(l.id)))
                ?.forEach((l) => {
                    l.eventsAndOutrightsLoaded = true;
                    l.oddsHeaders = getHeaders(null, l, false, true, eventsByLeagueFunction(l.id));
                    updateEventsOddsByHeaders(l, eventsByLeagueFunction);
                    l.selectedOdd = l.selectedOdd ?? l.oddsHeaders[0]?.groupCode ?? null;
                    if (error) {
                        l.errorLoadingEvents = true;
                    }
                });
        }

        async function updateEventsOddsByHeaders(
            league: PrematchDrilldownNode,
            eventsByLeagueFunction: (id: string) => PrematchEventDrilldown[],
        ) {
            const events = eventsByLeagueFunction(league.id);
            events.forEach((event) => {
                if (!event.oddsByHeaders?.length) event.oddsByHeaders = getOddsByHeaders(league, event, true);
            });
        }

        const setEvents = (loadedEvents: EventDrilldown[], eventsValue: PrematchEventDrilldown[]) => {
            if (!loadedEvents) return;
            loadedEvents.forEach((event) => {
                const existingEvent = eventsValue.find((e) => e.id === event.id);
                if (existingEvent) {
                    Object.assign(existingEvent, event);
                } else {
                    eventsValue.push(event);
                }
            });
        };

        const setOutrights = (loadedOutrights: Event[], outrightsValue: Event[]) => {
            if (!loadedOutrights) return;
            loadedOutrights
                .filter((outright) => outright.status === "ACTIVE" && outright.active)
                .forEach((outright) => {
                    const existingOutright = outrightsValue.find((o) => o.id === outright.id);
                    if (existingOutright) {
                        Object.assign(existingOutright, outright);
                    } else {
                        outrightsValue.push(outright);
                    }
                });
        };

        const setHeadersSports = (
            updatedSports: Set<string>,
            sportsValue: PrematchDrilldownNode[],
            eventsValue: PrematchEventDrilldown[],
        ) => {
            if (!updatedSports) return;
            updatedSports.forEach((sportId) => {
                const sport = sportsValue.find((s) => s.id === sportId);
                if (sport) {
                    // Get all events for this sport
                    const sportEvents = eventsValue.filter((e) => e.drilldownNodes[2].id === sportId);

                    // Find lowest displayOrder market across all events in this sport
                    let lowestDisplayOrderMarket = null;
                    let lowestDisplayOrder = Infinity;

                    sportEvents.forEach((event) => {
                        if (event.groupedMarkets?.length) {
                            const eventLowestMarket = event.groupedMarkets.reduce((lowest, current) => {
                                return current.displayOrder !== undefined &&
                                    current.displayOrder < (lowest.displayOrder || Infinity)
                                    ? current
                                    : lowest;
                            }, event.groupedMarkets[0]);

                            if (
                                eventLowestMarket &&
                                eventLowestMarket.displayOrder !== undefined &&
                                eventLowestMarket.displayOrder < lowestDisplayOrder
                            ) {
                                lowestDisplayOrderMarket = eventLowestMarket;
                                lowestDisplayOrder = eventLowestMarket.displayOrder;
                            }
                        }
                    });

                    // Save the information to the sport
                    sport.lowestDisplayOrderMarket = lowestDisplayOrderMarket;

                    // Continue with existing code
                    sport.oddsHeaders = getHeaders(null, sport, false, true, sportEvents);
                }
            });
        };

        const setHeadersCountries = (
            updatedCountries: Set<string>,
            countriesValue: PrematchDrilldownNode[],
            eventsValue: PrematchEventDrilldown[],
        ) => {
            if (!updatedCountries) return;
            updatedCountries.forEach((countryId) => {
                const country = countriesValue.find((c) => c.id === countryId);
                if (country) {
                    country.oddsHeaders = getHeaders(
                        null,
                        country,
                        false,
                        true,
                        eventsValue.filter((e) => e.drilldownNodes[1].id === country.id),
                    );
                }
            });
        };

        watch(activeTimeFilter, async (newVal, oldVal) => {
            if (newVal !== oldVal && oldVal !== undefined) {
                prematchLeftLoaderActive.value = true; //might be useful later - it takes hell of a time to load sometimes - TODO: display skeletons while loading
                storeLoaded.value = false;
                await fetchLeagues({ ...params, startTimeRange: newVal ?? undefined });
                storeLoaded.value = true;
                prematchLeftLoaderActive.value = false;
            }
        });

        watch(activeSport, (value) => {
            if (value) {
                analyticsPageObj.gameCode = value.code;
                analyticsPageObj.gameCodeStrict = value.code;
            } else {
                analyticsPageObj.gameCode = Constants.NoGame;
                analyticsPageObj.gameCodeStrict = Constants.NoGame;
            }
        });

        watch(
            () => getRouteIds.value.sportId,
            () => {
                startPeriodicRefresh();
            },
        );

        onUnmounted(() => {
            stopPeriodicRefresh();
        });

        setTimeout(() => {
            fetchLeagues({ ...params, startTimeRange: activeTimeFilter.value ?? undefined }).then(() => {
                startPeriodicRefresh();
            });
        });

        return {
            //state
            sportsError,
            favoriteEventsLoaded,
            sportsLoaded,
            favoriteLeaguesLoaded,
            sportsCount,
            storeLoaded,
            liveEventsLoadedLeaguesIds,
            events,
            outrights,
            leagues,
            countries,
            sports,
            filteredIds,
            activeTimeFilter,

            //getters
            getEvents,
            getEventsByLeague,
            getOutrightsByLeague,
            getSportById,
            getEventById,
            getLeagueById,
            getLeaguesBySport,
            getLeaguesByCountry,
            getLeaguesCountBySport,
            getEventCountsByLeagueAndSelectedTime,
            getCountriesBySport,
            getSpecialCategories,
            getEventsCountFromLeagues,
            getEventsCountFromLeague,
            getEventsCountFromCountry,
            filteredSports,
            filteredSportsNoDisplayOrder,
            filteredLeagues,
            filteredCountries,
            isFilterLoading,
            leagueHasTrefik,
            getEventsAndOutrightsBySportCodeAndLeagueIds,
            loadLeagues,
            //actions
            waitUntilInitialized,
            waitUntilEventInitialized,
            updateLeagues,
            setEvents,
            setOutrights,
            setHeadersSports,
            setHeadersCountries,
        };
    },
    {
        persist: {
            pick: ["sportsCount"],
            storage: localStorage,
        },
    },
);

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePrematchStore, import.meta.hot));
}
